import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Users, TrendingUp, Globe, Loader2, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid,
} from "recharts";

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toString();
}

interface Origin {
  country: string;
  refugees: number;
  asylumSeekers: number;
  idps: number;
  total: number;
}

interface TrendPoint {
  year: number;
  total: number;
}

interface UNHCRData {
  year: number;
  globalTotal: number;
  origins: Origin[];
  trends: Record<string, TrendPoint[]>;
}

export default function RefugeeTracker() {
  const { data, isLoading, error } = useQuery<UNHCRData>({
    queryKey: ["unhcr-data"],
    queryFn: async () => {
      const CACHE_KEY = "unhcr-data-cache";
      try {
        const { data, error } = await supabase.functions.invoke("fetch-unhcr");
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
        return data;
      } catch (err) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) return JSON.parse(cached);
        throw err;
      }
    },
    retry: 3,
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 15000),
    staleTime: 60 * 60_000,
    refetchInterval: 60 * 60_000,
  });

  const trendCountry = data?.origins?.[0]?.country;
  const trendData = trendCountry ? data?.trends?.[trendCountry] ?? [] : [];

  return (
    <section className="py-8 border-t border-primary/10">
      <div className="container mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Refugee & Displacement Tracker</h2>
            <p className="text-xs text-muted-foreground">
              UNHCR data — forcibly displaced populations by country of origin
            </p>
          </div>
          <Badge variant="outline" className="ml-auto text-[10px] font-mono">
            <Globe className="h-3 w-3 mr-1" />
            {data?.year ?? "—"}
          </Badge>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading UNHCR data…
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-4">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <p className="text-sm text-destructive">Failed to load data: {(error as Error).message}</p>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            {/* Global stat */}
            <div className="rounded-lg border border-primary/10 bg-card/50 p-4 text-center">
              <p className="text-3xl font-bold font-mono text-foreground">{formatNumber(data.globalTotal)}</p>
              <p className="text-xs text-muted-foreground mt-1">Total displaced persons (top 15 origins)</p>
            </div>

            {/* Bar chart — top origins */}
            <div className="rounded-lg border border-primary/10 bg-card/50 p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Top Countries of Origin</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.origins.slice(0, 10).map(o => ({
                      name: o.country.length > 12 ? o.country.slice(0, 12) + "…" : o.country,
                      Refugees: o.refugees,
                      "Asylum Seekers": o.asylumSeekers,
                      IDPs: o.idps,
                    }))}
                    layout="vertical"
                    margin={{ left: 80, right: 10, top: 5, bottom: 5 }}
                  >
                    <XAxis type="number" tickFormatter={formatNumber} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={75} />
                    <Tooltip formatter={(v: number) => formatNumber(v)} />
                    <Bar dataKey="Refugees" stackId="a" fill="hsl(var(--primary))" />
                    <Bar dataKey="Asylum Seekers" stackId="a" fill="hsl(var(--primary) / 0.5)" />
                    <Bar dataKey="IDPs" stackId="a" fill="hsl(var(--primary) / 0.25)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex gap-4 justify-center mt-2 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-primary" /> Refugees</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-primary/50" /> Asylum Seekers</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-primary/25" /> IDPs</span>
              </div>
            </div>

            {/* Trend line — top origin */}
            {trendData.length > 1 && (
              <div className="rounded-lg border border-primary/10 bg-card/50 p-4">
                <h3 className="text-sm font-semibold text-foreground mb-1">
                  <TrendingUp className="h-3.5 w-3.5 inline mr-1" />
                  5-Year Trend — {trendCountry}
                </h3>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ left: 10, right: 10, top: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--primary) / 0.1)" />
                      <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={formatNumber} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v: number) => formatNumber(v)} />
                      <Line type="monotone" dataKey="total" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Detail table */}
            <div className="rounded-lg border border-primary/10 bg-card/50 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-primary/10 bg-muted/30">
                      <th className="text-left px-3 py-2 font-semibold text-foreground">Country</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground">Refugees</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground">Asylum</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground">IDPs</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.origins.map((o) => (
                      <tr key={o.country} className="border-b border-primary/5 hover:bg-muted/20">
                        <td className="px-3 py-2 font-medium text-foreground">{o.country}</td>
                        <td className="text-right px-3 py-2 text-muted-foreground font-mono">{formatNumber(o.refugees)}</td>
                        <td className="text-right px-3 py-2 text-muted-foreground font-mono">{formatNumber(o.asylumSeekers)}</td>
                        <td className="text-right px-3 py-2 text-muted-foreground font-mono">{formatNumber(o.idps)}</td>
                        <td className="text-right px-3 py-2 font-mono font-bold text-foreground">{formatNumber(o.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
